import { useRef, useCallback, useEffect, useState } from 'react';
import { BackgroundProcessor } from '@livekit/track-processors';

const BG_STORAGE_KEY = 'pf-background-selection';

/**
 * Hook managing BackgroundProcessor lifecycle for LiveKit video tracks.
 * Handles lazy creation, mode switching, localStorage persistence, and cleanup.
 */
export function useBackgroundProcessor() {
  const processorRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  /** Lazily create and return the processor singleton. */
  const getProcessor = useCallback(() => {
    if (!processorRef.current) {
      processorRef.current = BackgroundProcessor({ mode: 'disabled' });
    }
    return processorRef.current;
  }, []);

  /** Read the saved background selection from localStorage, or null. */
  const getSavedSelection = useCallback(() => {
    try {
      const raw = localStorage.getItem(BG_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  /** Persist the current selection. */
  const saveSelection = useCallback((bg) => {
    try {
      localStorage.setItem(
        BG_STORAGE_KEY,
        JSON.stringify({
          id: bg.id,
          type: bg.type,
          ...(bg.blurRadius != null && { blurRadius: bg.blurRadius }),
          ...(bg.src != null && { src: bg.src }),
        }),
      );
    } catch {
      // localStorage full — non-critical
    }
  }, []);

  /**
   * Apply a background to a video track.
   * @param {import('livekit-client').LocalVideoTrack} videoTrack
   * @param {{ id: string, type: 'none'|'blur'|'image', blurRadius?: number, src?: string }} bg
   */
  const applyBackground = useCallback(
    async (videoTrack, bg) => {
      if (!videoTrack || !bg) return;

      setError(null);
      setIsProcessing(true);

      try {
        const processor = getProcessor();

        // Attach processor to track if not already attached
        if (!videoTrack.getProcessor()) {
          await videoTrack.setProcessor(processor);
        }

        switch (bg.type) {
          case 'none':
            await processor.switchTo({ mode: 'disabled' });
            break;
          case 'blur':
            await processor.switchTo({
              mode: 'background-blur',
              blurRadius: bg.blurRadius ?? 10,
            });
            break;
          case 'image':
            if (!bg.src) throw new Error('Background image source is required');
            await processor.switchTo({
              mode: 'virtual-background',
              imagePath: bg.src,
            });
            break;
          default:
            await processor.switchTo({ mode: 'disabled' });
        }

        saveSelection(bg);
      } catch (err) {
        console.error('[useBackgroundProcessor] Failed to apply background:', err);
        setError(err);
      } finally {
        setIsProcessing(false);
      }
    },
    [getProcessor, saveSelection],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      processorRef.current = null;
    };
  }, []);

  return { applyBackground, getProcessor, getSavedSelection, isProcessing, error };
}
